import {
  SIGNALSKIT_GLOBAL_CLICK_SKIP_SELECTOR,
  classifySignalsKitClickDestination,
  normalizeSignalsKitClickLabel
} from '~/utils/signalskitClickTracking'
import { getElementViewportMetrics, getTimingMetrics } from '~/utils/signalskitViewport'

const CLICK_DEDUPE_WINDOW_MS = 700

const resolveClickSurface = (anchor: HTMLAnchorElement) => {
  if (anchor.closest('header, [role="banner"], .hd')) return 'header'
  if (anchor.closest('footer, [role="contentinfo"]')) return 'footer'
  if (anchor.closest('nav, [role="navigation"]')) return 'navigation'
  if (anchor.closest('[data-home-permanent-collections]')) return 'home_permanent_collections'
  if (anchor.closest('[data-home-preorder-drops]')) return 'home_preorder_drops'
  if (anchor.closest('main')) return 'main'
  return 'page'
}

const isHeaderOrFooterSocial = (
  anchor: HTMLAnchorElement,
  eventName: string
) => eventName === 'social_click' && Boolean(anchor.closest('header, footer, [role="banner"], [role="contentinfo"], .hd'))

export default defineNuxtPlugin((nuxtApp) => {
  if (typeof window === 'undefined') return

  const route = useRoute()
  const locale = useSiteLocale()
  const signalsKit = useSignalsKit()
  const mountedAt = Date.now()
  let lastClickKey = ''
  let lastClickAt = 0

  const handleClick = (event: MouseEvent) => {
    if (event.defaultPrevented || event.button !== 0) return

    const target = event.target instanceof Element ? event.target : null
    const anchor = target?.closest('a[href]') as HTMLAnchorElement | null
    if (!anchor) return
    if (anchor.closest(SIGNALSKIT_GLOBAL_CLICK_SKIP_SELECTOR)) return

    const destination = classifySignalsKitClickDestination(
      anchor.getAttribute('href'),
      window.location.href
    )
    if (!destination) return
    if (isHeaderOrFooterSocial(anchor, destination.eventName)) return

    const clickKey = `${destination.eventName}:${destination.destination}:${route.fullPath}`
    const now = Date.now()
    if (clickKey === lastClickKey && now - lastClickAt < CLICK_DEDUPE_WINDOW_MS) return
    lastClickKey = clickKey
    lastClickAt = now

    signalsKit.trackEditorialSignal(destination.eventName, {
      page_path: route.path,
      route_full_path: route.fullPath,
      locale: locale.value,
      interaction_type: 'click',
      click_source: 'global_link_click_listener',
      link_surface: resolveClickSurface(anchor),
      link_label: normalizeSignalsKitClickLabel(
        anchor.textContent || anchor.getAttribute('aria-label') || anchor.title,
        destination.destination
      ),
      destination: destination.destination,
      destination_kind: destination.destinationKind,
      outbound: destination.outbound,
      product_type: destination.productType,
      social_platform: destination.socialPlatform,
      signalskit_client_available: typeof (window as any).signalsKit?.track === 'function',
      ...getElementViewportMetrics(anchor),
      ...getTimingMetrics(mountedAt)
    })
  }

  window.addEventListener('click', handleClick, { capture: true })

  nuxtApp.hook('app:unmounted', () => {
    window.removeEventListener('click', handleClick, { capture: true })
  })
})
